import type { I18n, MessageDescriptor } from "@lingui/core";

import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Info } from "lucide-react";
import { observer } from "mobx-react-lite";
import { type ReactNode, useState } from "react";

import { cn } from "@/utils/shadcn-ui";

import type { MeasurementColumnSpec } from "./measurement-columns";
import type { MeasurementRow, Measurements } from "./measurements";

import {
  apiPutPpgUuid,
  type MeasurementSession,
  type PPGSignalInfo,
  PPGQualityFlag,
  QualityLevel,
  SessionKind,
  type ValueCell,
} from "../../../api";
import { NativeSelect } from "../../../components/ui-kit/base/native-select";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../../components/ui-kit/dialog";
import { BaseTextarea } from "../../../components/ui-kit/fields/base-textarea";
import { Label } from "../../../components/ui-kit/fields/label";
import { Form } from "../../../components/ui-kit/form/form";
import { ppgQuality } from "../../../constants/enum-to-text";
import {
  EM_DASH,
  formatMeasurement,
  positionText,
  QualityDot,
  VARIANT_LABELS,
} from "./measurement-cells";
import { MEASUREMENT_COLUMNS } from "./measurement-columns";

interface Draft {
  /** A plain number, as the payload carries it. */
  quality_flag: number;
  comment: string;
}

/**
 * Per-position breakdown of one session, laid out after the spec's mockup: the
 * pair's values up top, then one card per position with its HR, AF, SpO₂ and
 * SQI, and the position's quality flag and comment. The page's only write — a
 * permitted user's edits land on that position's PPG row through the existing
 * `PUT /ppg/{uuid}`.
 */
export const MeasurementDialog = observer(function MeasurementDialog({
  model,
  row,
  onClose,
}: {
  model: Measurements;
  row: MeasurementRow;
  onClose: () => void;
}) {
  const { t, i18n } = useLingui();
  const session = row.session;
  // By position, then oldest first within one — a session can hold the same
  // position twice (a re-measurement inside the window).
  const signals = [...(session.ppg_signals ?? [])].sort(
    (a, b) =>
      a.measurement_condition.localeCompare(b.measurement_condition) ||
      a.measured_at.localeCompare(b.measured_at),
  );
  const repeated = new Set(
    signals
      .map((signal) => signal.measurement_condition)
      .filter((condition, index, all) => all.indexOf(condition) !== index),
  );
  const standalone = session.kind === SessionKind.standalone;
  const [drafts, setDrafts] = useState<Record<string, Draft>>(() =>
    Object.fromEntries(
      signals.map((signal) => [
        signal.ppg_uuid,
        {
          quality_flag: signal.quality_flag.flag,
          comment: signal.comment ?? "",
        },
      ]),
    ),
  );
  const editable = signals.some((signal) => signal.editable);
  const changed = signals.filter((signal) =>
    isChanged(signal, drafts[signal.ppg_uuid]),
  );

  async function save() {
    let saved = 0;
    for (const signal of changed) {
      const draft = drafts[signal.ppg_uuid];
      try {
        await apiPutPpgUuid({
          path: { uuid: signal.ppg_uuid },
          // Only what changed: a comment-only edit must not re-send the flag,
          // which would start a cascade to the derived rows for nothing.
          body: {
            ...(draft.quality_flag !== signal.quality_flag.flag && {
              quality_flag: draft.quality_flag,
            }),
            ...(draft.comment !== (signal.comment ?? "") && {
              comment: draft.comment || null,
            }),
          },
        });
        saved += 1;
      } catch (error) {
        // The dialog stays open with everything typed, so nothing is lost.
        if (saved > 0) void model.refresh();
        window.handleError(error);
        return;
      }
    }
    // A flag change cascades to derived and computed rows asynchronously, so
    // the day is refetched rather than patched here.
    await model.refresh();
    onClose();
  }

  const when = new Intl.DateTimeFormat(i18n.locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(session.session_start));
  const count = signals.length;
  const hrCondition = model.selected("hr_condition");
  const spo2Condition = model.selected("spo2_condition");
  // The table reads the newest signal of the selected condition, so a
  // repeated position is shown in the table by its last reading only. The
  // accent picks that one out among several; a lone card has nothing to pick
  // out, and a standalone reading doesn't feed the day's figures.
  const tableSignal =
    count > 1 && !standalone
      ? signals
          .filter((signal) => signal.measurement_condition === hrCondition)
          .pop()
      : undefined;
  const shownInTable = (signal: PPGSignalInfo) => signal === tableSignal;

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      // Sized to the cards, one column each up to three: a single card would
      // otherwise stretch its rows far apart, and positions side by side are
      // what makes them comparable.
      width={count === 1 ? "440px" : count === 2 ? "720px" : "1040px"}
    >
      <Form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={editable ? () => save() : undefined}
      >
        <DialogHeader
          description={
            <>
              {when} ·{" "}
              {t`${plural(count, { one: "# position", other: "# positions" })}`}
            </>
          }
        >
          {standalone ? (
            <Trans>Standalone PPG reading</Trans>
          ) : (
            <Trans>PPG recording detail</Trans>
          )}
        </DialogHeader>

        {/* Edge to edge and outside the body, so it stays put while only
            the cards scroll. */}
        {!standalone && <PairStrip session={session} />}

        <DialogBody>
          <div
            className={cn(
              "grid gap-3",
              count === 2 && "sm:grid-cols-2",
              count > 2 && "sm:grid-cols-2 lg:grid-cols-3",
            )}
          >
            {signals.map((signal) => (
              <PositionCard
                key={signal.ppg_uuid}
                model={model}
                signal={signal}
                standalone={standalone}
                repeated={repeated.has(signal.measurement_condition)}
                shownInTable={shownInTable(signal)}
                draft={drafts[signal.ppg_uuid]}
                onChange={(draft) =>
                  setDrafts((current) => ({
                    ...current,
                    [signal.ppg_uuid]: draft,
                  }))
                }
              />
            ))}
          </div>
        </DialogBody>

        <DialogFooter>
          <TableSourceNote
            signals={signals}
            standalone={standalone}
            hrCondition={hrCondition}
            spo2Condition={spo2Condition}
          />
          {editable ? (
            <>
              <Button type="button" onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={changed.length === 0}
              >
                <Trans>Save</Trans>
              </Button>
            </>
          ) : null}
        </DialogFooter>
      </Form>
    </Dialog>
  );
});

/**
 * The values only the pair yields — DRI and LVFP come from Condition 2 against
 * Condition 1, not from any one position — so they sit above the cards rather
 * than in one of them. Only the columns the API sent, only when there's one.
 */
function PairStrip({ session }: { session: MeasurementSession }) {
  const { i18n } = useLingui();
  const pair = MEASUREMENT_COLUMNS.filter((spec) =>
    ["dri", "lvfp", "lvfp_median"].includes(spec.key),
  ).flatMap((spec) => {
    const cell = session.cells?.[spec.key];
    const value = cell && "value" in cell ? cell.value : null;
    return typeof value === "number" ? [{ spec, value }] : [];
  });
  if (pair.length === 0) return null;

  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-b bg-muted px-6 py-3 max-sm:px-4">
      {pair.map(({ spec, value }) => (
        <span key={spec.key} className="inline-flex items-baseline gap-1.5">
          <span className="text-xs text-muted-foreground">
            {labelText(spec, i18n)}
          </span>
          <span className="text-base font-bold">
            {formatMeasurement(value, spec, i18n.locale)}
          </span>
          {spec.unit && (
            <span className="text-xs text-muted-foreground">{spec.unit}</span>
          )}
        </span>
      ))}
      <span className="text-xs text-muted-foreground">
        <Trans>from the standing and supine pair</Trans>
      </span>
    </div>
  );
}

function PositionCard({
  model,
  signal,
  standalone,
  repeated,
  shownInTable,
  draft,
  onChange,
}: {
  model: Measurements;
  signal: PPGSignalInfo;
  standalone: boolean;
  /** Another card of the session has the same position: tell them apart. */
  repeated: boolean;
  shownInTable: boolean;
  draft: Draft;
  onChange: (draft: Draft) => void;
}) {
  const { t, i18n } = useLingui();
  const position = positionText(signal.position, i18n);
  const device = signal.measurement_device;
  const hrAlgorithm = model.selected("hr_algorithm");
  const afModel = model.selected("af_model");
  const hr = hrAlgorithm ? signal.derived?.hr?.[hrAlgorithm] : undefined;
  const af = afModel ? signal.derived?.af_probability?.[afModel] : undefined;
  const spo2 = signal.derived?.spo2?.device;
  const length = new Intl.NumberFormat(i18n.locale, {
    maximumFractionDigits: 0,
  }).format(signal.length);
  const time = new Intl.DateTimeFormat(i18n.locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(signal.measured_at));
  const flagId = `flag-${signal.ppg_uuid}`;
  const commentId = `comment-${signal.ppg_uuid}`;

  return (
    <section
      data-testid={`position-${signal.position}`}
      className={cn(
        "overflow-hidden rounded-lg border",
        // The one accent: which position the table's HR and AF are read from.
        shownInTable && "border-brand-primary",
      )}
    >
      <h4
        className={cn(
          // Each piece stays whole; when they don't fit on one line the
          // device and length move to a line of their own.
          "flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b px-3 py-2 text-sm font-bold",
          "bg-muted",
        )}
      >
        <span className="whitespace-nowrap first-letter:uppercase">
          {standalone ? (
            <Trans>{position} · standalone</Trans>
          ) : repeated ? (
            <Trans>
              {position} · {time}
            </Trans>
          ) : (
            position
          )}
        </span>
        {shownInTable && (
          <span className="whitespace-nowrap rounded border border-brand-primary bg-background px-1.5 text-xs font-semibold text-brand-primary-hover">
            <Trans>Shown in table</Trans>
          </span>
        )}
        <span className="ml-auto whitespace-nowrap text-xs font-normal text-muted-foreground">
          {device} · <Trans>{length} s</Trans>
        </span>
      </h4>

      <div className="flex flex-col gap-3 px-3 py-3">
        <dl className="text-sm">
          <MetricRow
            label={
              hrAlgorithm ? (
                <Trans>HR ({VARIANT_LABELS[hrAlgorithm] ?? hrAlgorithm})</Trans>
              ) : (
                <Trans>HR</Trans>
              )
            }
            cell={hr}
            unit="bpm"
          />
          <MetricRow
            label={
              afModel ? (
                <Trans>
                  AF probability ({VARIANT_LABELS[afModel] ?? afModel})
                </Trans>
              ) : (
                <Trans>AF probability</Trans>
              )
            }
            cell={af}
            unit="%"
            scale={100}
          />
          <MetricRow label={<Trans>SpO₂</Trans>} cell={spo2} unit="%" />
          <div className="flex items-center justify-between py-1.5">
            <dt className="text-muted-foreground">
              <Trans>SQI</Trans>
            </dt>
            {/* Shown, never edited: the flag below may override it. */}
            <dd>
              <SqiMeter
                sqi={signal.computed_sqi}
                level={signal.quality_flag.level}
              />
            </dd>
          </div>
        </dl>

        <div className="flex flex-col gap-1">
          <Label htmlFor={flagId}>
            <Trans>Quality flag</Trans>
          </Label>
          {signal.editable ? (
            <NativeSelect
              id={flagId}
              value={draft.quality_flag}
              onChange={(event) =>
                onChange({
                  ...draft,
                  quality_flag: Number(event.target.value),
                })
              }
            >
              {QUALITY_FLAGS.map((flag) => (
                <option key={flag} value={flag}>
                  {i18n._(ppgQuality[flag])}
                </option>
              ))}
            </NativeSelect>
          ) : (
            <span
              id={flagId}
              className="inline-flex items-center gap-1.5 text-sm"
            >
              <QualityDot level={signal.quality_flag.level} />
              {flagText(signal, i18n)}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor={commentId}>
            <Trans>Comment</Trans>
          </Label>
          {signal.editable ? (
            <BaseTextarea
              id={commentId}
              rows={4}
              autosize
              value={draft.comment}
              // The comment lands on this position's PPG row only — not on the
              // derived or computed rows — so its name says which one.
              aria-label={t`Comment on the ${position} PPG (${device})`}
              placeholder={t`Note on the ${position} recording…`}
              onChange={(event) =>
                onChange({ ...draft, comment: event.target.value })
              }
            />
          ) : (
            <span id={commentId} className="text-sm whitespace-pre-wrap">
              {signal.comment || EM_DASH}
            </span>
          )}
        </div>

        <HrByAlgorithm values={signal.derived?.hr} />
      </div>
    </section>
  );
}

function MetricRow({
  label,
  cell,
  unit,
  scale = 1,
}: {
  label: ReactNode;
  cell: ValueCell | undefined;
  unit: string;
  scale?: number;
}) {
  const { i18n } = useLingui();
  const value = cell?.value;
  return (
    <div className="flex items-center justify-between border-b py-1.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "font-semibold",
          value == null && "text-muted-foreground",
        )}
      >
        {value == null
          ? EM_DASH
          : `${new Intl.NumberFormat(i18n.locale, { maximumFractionDigits: 0 }).format(value * scale)} ${unit}`}
      </dd>
    </div>
  );
}

/** SQI out of 10 as a short bar, coloured by the flag's level. */
function SqiMeter({
  sqi,
  level,
}: {
  sqi: number | null | undefined;
  level: QualityLevel;
}) {
  const { i18n } = useLingui();
  if (sqi == null)
    return <span className="text-muted-foreground">{EM_DASH}</span>;
  const percent = Math.max(0, Math.min(100, sqi * 10));
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-block h-1.5 w-14 overflow-hidden rounded-full bg-border">
        <span
          className={cn("block h-full", METER[level])}
          style={{ width: `${percent}%` }}
        />
      </span>
      <span className="font-semibold">
        {new Intl.NumberFormat(i18n.locale, {
          maximumFractionDigits: 1,
        }).format(sqi)}
        <span className="text-xs font-normal text-muted-foreground"> / 10</span>
      </span>
    </span>
  );
}

const METER: Record<QualityLevel, string> = {
  [QualityLevel.ok]: "bg-success-solid",
  [QualityLevel.warn]: "bg-warning-solid",
  [QualityLevel.bad]: "bg-danger-solid",
};

/** Every HR algorithm the payload carries for a position. */
function HrByAlgorithm({
  values,
}: {
  values: Record<string, ValueCell> | undefined;
}) {
  const { i18n } = useLingui();
  const entries = Object.entries(values ?? {}).filter(
    ([, cell]) => cell.value != null,
  );
  if (entries.length === 0) return null;
  const format = new Intl.NumberFormat(i18n.locale, {
    maximumFractionDigits: 0,
  });
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">
        <Trans>HR by algorithm</Trans>
      </span>
      <dl className="text-sm">
        {entries.map(([variant, cell]) => (
          <div key={variant} className="flex justify-between py-0.5">
            <dt className="text-muted-foreground">
              {VARIANT_LABELS[variant] ?? variant}
            </dt>{" "}
            <dd className="font-semibold">
              {format.format(cell.value ?? 0)} bpm
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Says which position the table's numbers come from — the page's condition
 * selectors decide it, so the dialog shouldn't leave it to guesswork. Nothing
 * to say for a standalone reading: it has the one position.
 */
function TableSourceNote({
  signals,
  standalone,
  hrCondition,
  spo2Condition,
}: {
  signals: PPGSignalInfo[];
  standalone: boolean;
  hrCondition: string | undefined;
  spo2Condition: string | undefined;
}) {
  const { i18n } = useLingui();
  const positionOf = (condition: string | undefined) => {
    const signal = signals.find(
      (item) => item.measurement_condition === condition,
    );
    return signal ? positionText(signal.position, i18n) : undefined;
  };
  const hr = positionOf(hrCondition);
  const spo2 = positionOf(spo2Condition);
  const hrRepeated =
    signals.filter((item) => item.measurement_condition === hrCondition)
      .length > 1;
  const note =
    standalone || !hr ? null : hrRepeated ? (
      <Trans>The table's values come from the latest {hr} reading.</Trans>
    ) : spo2 && spo2 !== hr ? (
      <Trans>
        The table's HR and AF come from the {hr} position, its SpO₂ from the{" "}
        {spo2} one.
      </Trans>
    ) : (
      <Trans>The table's values come from the {hr} position.</Trans>
    );

  return (
    <span className="flex flex-1 items-center gap-1.5 text-xs text-muted-foreground">
      {note && (
        <>
          <Info size={12} aria-hidden />
          {note}
        </>
      )}
    </span>
  );
}

/** The flag select offers the whole enum, in its own order. */
const QUALITY_FLAGS = Object.values(PPGQualityFlag).filter(
  (value): value is PPGQualityFlag => typeof value === "number",
);

function labelText(spec: MeasurementColumnSpec, i18n: I18n): string {
  return typeof spec.label === "string" ? spec.label : i18n._(spec.label);
}

function flagText(signal: PPGSignalInfo, i18n: I18n): string {
  // A flag newer than this build falls back to the API's own name for it.
  const label = (ppgQuality as Partial<Record<number, MessageDescriptor>>)[
    signal.quality_flag.flag
  ];
  return label ? i18n._(label) : signal.quality_flag.name;
}

function isChanged(signal: PPGSignalInfo, draft: Draft | undefined): boolean {
  return (
    !!draft &&
    signal.editable &&
    (draft.quality_flag !== signal.quality_flag.flag ||
      draft.comment !== (signal.comment ?? ""))
  );
}
